import {
  ProviderStatus,
  useBooleanFlagValue,
  useOpenFeatureClientStatus,
  useStringFlagValue,
} from '@openfeature/react-sdk';

import { getFeatureFlagsConfig } from './config';

/**
 * Boolean flag hook — a WRAPPER over `useBooleanFlagValue`, not a re-export
 * (design D8): the injected `forceEnableAll` override is honored at this one
 * choke point instead of being re-read from env across declarations.
 *
 * Takes NO domain parameter — the domain is INHERITED from the React tree
 * (design D7/D8), so a subtree wrapped in `<OpenFeatureProvider domain="…">`
 * resolves through that domain's provider using these same standard hooks.
 * Pinning the default domain here would break those subtrees — none exist
 * today, but the inheritance is the door's contract, not an accident.
 */
export const useBooleanFlag = (key: string, defaultValue: boolean): boolean => {
  const value = useBooleanFlagValue(key, defaultValue);
  return getFeatureFlagsConfig().forceEnableAll || value;
};

/**
 * String flag hook — thin wrapper over `useStringFlagValue`. Unaffected by
 * `forceEnableAll` (boolean-only override). Domain is tree-inherited, as
 * above.
 */
export const useStringFlag = (key: string, defaultValue: string): string =>
  useStringFlagValue(key, defaultValue);

/**
 * Whether the OpenFeature provider has settled (READY or ERROR — anything
 * other than NOT_READY / RECONCILING). While `false`, flag hooks still return
 * their defaults, so callers that make irreversible decisions on a flag value
 * (e.g. the Healthdirect launch flow) should wait for `true` before acting
 * rather than treating an unresolved default as a settled read. ERROR counts
 * as resolved so such callers fall back to the flag default instead of
 * waiting forever. (Moved verbatim from `@/features/flags` at the F-series;
 * reads the tree-inherited domain's client status.)
 */
export const useAreFeatureFlagsResolved = () => {
  const flagStatus = useOpenFeatureClientStatus();
  return (
    flagStatus !== ProviderStatus.NOT_READY && flagStatus !== ProviderStatus.RECONCILING
  );
};
